import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Breadcrumbs,
  Link,
  Container
} from '@mui/material';
import { CloudUpload, Home as HomeIcon } from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';

interface UploadedFile {
  constructionId: string;
  fileName: string;
  fileSizeBytes: number;
  sheets: string[];
}

export const DocumentIdentifierSystemPage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [identifying, setIdentifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Upload state
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  
  // Identify form state
  const [identifyForm, setIdentifyForm] = useState({
    worksheetName: '',
    startCell: '',
    endCell: '',
    comparer: 'Contains',
    searchString: '',
    documentName: '',
    documentIdentifier: ''
  });
  
  const { authenticatedFetch } = useAuthenticatedFetch();
  const navigate = useNavigate();
  
  const handleFileUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    setError(null);
    setSuccess(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await authenticatedFetch('/api/appraisals/construction/xlsx/create', {
        method: 'POST',
        body: formData
      });
      
      if (!response) {
        throw new Error('Failed to upload file - authentication error');
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }
      
      const data = await response.json();
      
      // Fetch worksheet names
      const sheetsResponse = await authenticatedFetch(
        `/api/appraisals/construction/xlsx/${data.constructionId}/sheets`
      );
      
      if (!sheetsResponse) {
        throw new Error('Failed to fetch worksheet names - authentication error');
      }
      
      if (!sheetsResponse.ok) {
        throw new Error('Failed to fetch worksheet names');
      }
      
      const sheetsData = await sheetsResponse.json();
      
      setUploadedFile({
        constructionId: data.constructionId,
        fileName: data.fileName,
        fileSizeBytes: data.fileSizeBytes,
        sheets: sheetsData.sheets
      });
      
      setSuccess(`File uploaded successfully! Construction ID: ${data.constructionId}`);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  
  const handleIdentifyDocument = async () => {
    if (!uploadedFile) return;

    setIdentifying(true);
    setError(null);

    try {
      // Prepare the payload - send null for worksheet_name if "First Worksheet in Workbook" is selected
      const payload = {
        ...identifyForm,
        worksheetName: identifyForm.worksheetName === 'First Worksheet in Workbook' ? null : identifyForm.worksheetName
      };

      const response = await authenticatedFetch(
        `/api/appraisals/construction/xlsx/${uploadedFile.constructionId}/identify`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          timeout: 60000 // 60 seconds for document identification
        }
      );
      
      if (!response) {
        throw new Error('Failed to identify document - request timeout or network error');
      }
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(`Document identified successfully! Redirecting to document details...`);
        // Redirect to Document Details Page
        navigate(`/admin/deliverables/doc-id-system/documents/${data.appraisalDocumentId}`);
      } else {
        setError(data.error || 'Identification failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during identification');
    } finally {
      setIdentifying(false);
    }
  };
  
  const handleReset = () => {
    setFile(null);
    setUploadedFile(null);
    setError(null);
    setSuccess(null);
    setIdentifyForm({
      worksheetName: '',
      startCell: '',
      endCell: '',
      comparer: 'Contains',
      searchString: '',
      documentName: '',
      documentIdentifier: ''
    });
  };
  
  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/deliverables"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/deliverables');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Deliverables Admin
          </Link>
          <Link
            color="inherit"
            href="/admin/deliverables/doc-id-system"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/deliverables/doc-id-system');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Document Identification System
          </Link>
          <Typography color="text.primary">New Document</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Create Document Identifier
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Upload Excel workbooks and identify specific documents within worksheets based on cell ranges and comparison criteria.
          </Typography>
        </Box>
        {/* Step 1: Upload Excel File */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Step 1: Upload Excel Workbook
          </Typography>

          {!uploadedFile ? (
            <>
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={{ display: 'none' }}
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<CloudUpload />}
                  disabled={uploading}
                  sx={{ mr: 2 }}
                >
                  Select Excel File (.xlsx)
                </Button>
              </label>

              {file && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Selected: {file.name} ({Math.round(file.size / 1024)} KB)
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleFileUpload}
                    disabled={uploading}
                    sx={{ mt: 1 }}
                  >
                    {uploading ? <CircularProgress size={20} /> : 'Upload File'}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Box>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6">{uploadedFile.fileName}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Construction ID: {uploadedFile.constructionId}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    File Size: {Math.round(uploadedFile.fileSizeBytes / 1024)} KB
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Worksheets: {uploadedFile.sheets.join(', ')}
                  </Typography>
                </CardContent>
              </Card>
              <Button variant="outlined" onClick={handleReset}>
                Upload Different File
              </Button>
            </Box>
          )}
        </Paper>

        {/* Step 2: Identify Document */}
        {uploadedFile && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Step 2: Identify Document
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  options={['First Worksheet in Workbook', ...uploadedFile.sheets]}
                  value={identifyForm.worksheetName}
                  onChange={(_, value) => setIdentifyForm({ ...identifyForm, worksheetName: value || '' })}
                  renderInput={(params) => (
                    <TextField {...params} label="Worksheet Name" required fullWidth helperText="Select 'First Worksheet in Workbook' to automatically use the first worksheet" />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="Starting Cell"
                  value={identifyForm.startCell}
                  onChange={(e) => setIdentifyForm({ ...identifyForm, startCell: e.target.value.toUpperCase() })}
                  placeholder="B3"
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="Ending Cell"
                  value={identifyForm.endCell}
                  onChange={(e) => setIdentifyForm({ ...identifyForm, endCell: e.target.value.toUpperCase() })}
                  placeholder="C12"
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Comparer</InputLabel>
                  <Select
                    value={identifyForm.comparer}
                    onChange={(e) => setIdentifyForm({ ...identifyForm, comparer: e.target.value })}
                    label="Comparer"
                  >
                    <MenuItem value="Equal">Equal</MenuItem>
                    <MenuItem value="StartsWith">Starts With</MenuItem>
                    <MenuItem value="Contains">Contains</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="Search String"
                  value={identifyForm.searchString}
                  onChange={(e) => setIdentifyForm({ ...identifyForm, searchString: e.target.value })}
                  placeholder="Total Revenue"
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="Document Name"
                  value={identifyForm.documentName}
                  onChange={(e) => setIdentifyForm({ ...identifyForm, documentName: e.target.value })}
                  placeholder="Q4 Revenue Report"
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="Document Identifier"
                  value={identifyForm.documentIdentifier}
                  onChange={(e) => setIdentifyForm({ ...identifyForm, documentIdentifier: e.target.value })}
                  placeholder="q4_revenue"
                  helperText="Letters and underscores only (max 50 chars)"
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleIdentifyDocument}
                  disabled={identifying || !identifyForm.worksheetName || !identifyForm.startCell || !identifyForm.endCell}
                  sx={{ mr: 2 }}
                >
                  {identifying ? <CircularProgress size={20} /> : 'Identify Document'}
                </Button>
                <Button variant="outlined" onClick={handleReset}>
                  Start Over
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}
      
        {/* Messages */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>
        )}
      </Box>
    </Container>
  );
};